import React, { createContext, useContext, useState, useEffect } from 'react';
import { authApi } from '../services/authApi';
import { clearSelectedOffice } from '../services/officeSelection';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [practice, setPractice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('dentalToken'));

  // Initialize authentication state
  useEffect(() => {
    const initAuth = async () => {
      const savedToken = localStorage.getItem('dentalToken');
      if (savedToken) {
        try {
          const response = await authApi.getCurrentUser(savedToken);
          setUser(response.user);
          setToken(savedToken);
          
          // If practice data is incomplete (missing critical fields), fetch from dashboard API
          if (!response.practice || !response.practice.officeHours || !response.practice.emergencyContact) {
            try {
              const practiceApi = (await import('../services/authApi')).practiceApi;
              const dashboardData = await practiceApi.getDashboard();
              
              if (dashboardData.success && dashboardData.data?.practice) {
                setPractice(dashboardData.data.practice);
              } else {
                setPractice(response.practice);
              }
            } catch (dashboardError) {
              if (dashboardError?.response?.status === 403) {
                localStorage.removeItem('dentalToken');
                setToken(null);
                setUser(null);
                setPractice(null);
                setLoading(false);
                return;
              }
              setPractice(response.practice);
            }
          } else {
            setPractice(response.practice);
          }
        } catch (error) {
          console.error('Token validation failed:', error);
          localStorage.removeItem('dentalToken');
          setToken(null);
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  const login = async (userData, authToken, practiceData = null) => {
    setUser(userData);
    setToken(authToken);
    localStorage.setItem('dentalToken', authToken);
    clearSelectedOffice();
    // Fresh login → reset per-session UI flags so announcements re-show.
    sessionStorage.removeItem('whatsNewShownThisSession');
    
    if (authToken && !practiceData) {
      try {
        const practiceApi = (await import('../services/authApi')).practiceApi;
        const dashboardData = await practiceApi.getDashboard();
        
        if (dashboardData.success && dashboardData.data?.practice) {
          setPractice(dashboardData.data.practice);
        } else {
          setPractice(practiceData);
        }
      } catch (error) {
        console.error('Failed to fetch practice data:', error);
        setPractice(practiceData);
      }
    } else {
      setPractice(practiceData);
    }
    
    return { success: true };
  };

  const registerPractice = async (practiceData) => {
    try {
      const response = await authApi.registerPractice(practiceData);
      
      setUser(response.user);
      setPractice(response.practice);
      setToken(response.token);
      
      localStorage.setItem('dentalToken', response.token);
      sessionStorage.removeItem('whatsNewShownThisSession');
      
      return { success: true, user: response.user, practice: response.practice };
    } catch (error) {
      console.error('Practice registration failed:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Registration failed' 
      };
    }
  };

  const logout = () => {
    clearSelectedOffice();
    setUser(null);
    setPractice(null);
    setToken(null);
    localStorage.removeItem('dentalToken');
    // Clear per-session UI flags so the next login shows fresh announcements/popups.
    sessionStorage.removeItem('whatsNewShownThisSession');
  };

  const isAuthenticated = () => {
    return !!user && !!token;
  };

  const isPracticeAdmin = () => {
    return user?.role === 'practice_admin';
  };

  const isPracticeStaff = () => {
    return user?.role === 'practice_admin' || user?.role === 'practice_staff';
  };

  const isPatient = () => {
    return user?.role === 'patient';
  };

  const refreshPractice = async () => {
    try {
      const practiceApi = (await import('../services/authApi')).practiceApi;
      const dashboardData = await practiceApi.getDashboard();
      
      if (dashboardData.success && dashboardData.data?.practice) {
        setPractice(dashboardData.data.practice);
        return dashboardData.data.practice;
      }
      return practice;
    } catch (error) {
      console.error('Error refreshing practice data:', error);
      return practice;
    }
  };

  const value = {
    user,
    practice,
    token,
    loading,
    login,
    registerPractice,
    logout,
    isAuthenticated,
    isPracticeAdmin,
    isPracticeStaff,
    isPatient,
    refreshPractice,
    markPasswordSet: () => setUser((u) => (u ? { ...u, mustChangePassword: false } : u)),
    markWelcomeVideoSeen: () => setUser((u) => (u ? { ...u, welcomeVideoSeen: true, showWelcomeChecklist: true } : u)),
    hideWelcomeChecklist: () => setUser((u) => (u ? { ...u, showWelcomeChecklist: false } : u))
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};